import React, { useEffect, useRef, useState } from 'react'

import moment from 'config/moment'
import DownloadableTitle from 'features/Main/NewDashboard/components/DownloadableTitle'
import GenderDonutChartsByLevel from 'features/Main/NewDashboard/ClientScanTab/ChartsByLevel/Gender/GenderDonutChartsByLevel'
import AgeDonutChartsByLevel from 'features/Main/NewDashboard/ClientScanTab/ChartsByLevel/Age/AgeDonutChartsByLevel'
import FoodPreferenceDonutChartsByLevel from 'features/Main/NewDashboard/ClientScanTab/ChartsByLevel/FoodPreference/FoodPreferenceDonutChartsByLevel'
import { useApi } from 'hooks'
import { ERRORS } from 'consts'
import { snackbar } from 'components'
import { LevelRegistrationEvolution } from 'features/Main/NewDashboard/OverviewTab/LevelRegistrationEvolution/LevelRegistrationEvolution'

import StoresAndDateFilter from '../StoresAndDateFilter/StoresAndDateFilter'
import FrequencyByLevel from './FrequencyByLevel/FrequencyByLevel'
import ActiveUsersByLevel from './ActiveUsersByLevel/ActiveUsersByLevel'
import UniqueUsersEvolution from './UniqueUsersEvolution/UniqueUsersEvolution'
import FrequencyEvolutionByLevel from './FrequencyEvolutionByLevel/FrequencyEvolutionByLevel'
import LevelChangeSummary from './LevelChangeSummary/LevelChangeSummary'
import { GenderDonutChart, AgeDonutChart, FoodPreferenceDonutChart } from './TagsAnalysis'
import { getStores } from '../StoresAndDateFilter/api'
import { GetStoresResponse, Store } from '../StoresAndDateFilter/types'
import { useDashboardFilters } from '../contexts/DashboardFiltersContext'

const ClientScanTab = () => {
  const {
    filters,
    handlePeriodChange,
    handleStoreChange,
    handleCompareStoreChange,
    handleComparaChange,
    handleApply,
    isActiveTab,
  } = useDashboardFilters()
  const { period, store, compareStore, compara, applyTrigger, isLoading } = filters

  const getStoresResponseGetter = (responseData: GetStoresResponse) => responseData?.stores ?? []
  const [{ data: stores }] = useApi<GetStoresResponse, Store[]>(
    getStores,
    getStoresResponseGetter,
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )

  const getStoreId = (storeName: string): number | undefined => {
    if (storeName === 'all') return undefined
    const storeItem = stores.find(s => s.name === storeName)
    return storeItem?.id
  }
  const storeId = getStoreId(store)
  const compareStoreId = getStoreId(compareStore)

  // Only trigger API calls if this tab is active
  const shouldTrigger = isActiveTab('client-scan')

  // Ejecutar indicadores secuencialmente: uno termina -> arranca el siguiente
  const [step, setStep] = useState(0)
  const runRef = useRef(0)

  useEffect(() => {
    if (!shouldTrigger) return
    runRef.current = applyTrigger
    setStep(0)
  }, [applyTrigger, shouldTrigger])

  const onLoadedGuard = (currentStep: number, nextStep: number) => {
    return () => {
      if (runRef.current !== applyTrigger) return
      setStep(prev => (prev === currentStep ? nextStep : prev))
    }
  }

  return (
    <div className="flex flex-col gap-8 p-6 bg-gray-50 min-h-screen">
      <div className="flex flex-row items-end gap-4 w-full">
        <StoresAndDateFilter
          period={period}
          onPeriodChange={handlePeriodChange}
          storesList={stores}
          store={store}
          onStoreChange={handleStoreChange}
          compara={compara}
          onComparaChange={handleComparaChange}
          compareStore={compareStore}
          onCompareStoreChange={handleCompareStoreChange}
          onApply={handleApply}
          isLoading={isLoading}
        />
      </div>
      <div className="flex flex-col gap-8">
        <FrequencyByLevel
          period={period}
          storeId={storeId}
          compara={compara}
          compareStoreId={compareStoreId}
          trigger={shouldTrigger && step === 0 ? applyTrigger : 0}
          onLoaded={onLoadedGuard(0, 1)}
        />
        <ActiveUsersByLevel
          period={period}
          storeId={storeId}
          compara={compara}
          compareStoreId={compareStoreId}
          trigger={shouldTrigger && step === 1 ? applyTrigger : 0}
          onLoaded={onLoadedGuard(1, 2)}
        />
        <UniqueUsersEvolution
          period={period}
          storeId={storeId}
          trigger={shouldTrigger && step === 2 ? applyTrigger : 0}
          onLoaded={onLoadedGuard(2, 3)}
        />
        <FrequencyEvolutionByLevel
          period={period}
          storeId={storeId}
          trigger={shouldTrigger && step === 3 ? applyTrigger : 0}
          onLoaded={onLoadedGuard(3, 4)}
        />
        <LevelRegistrationEvolution
          period1={{
            startDate: moment()
              .year(period.year)
              .month(period.month)
              .startOf('month'),
            endDate: moment()
              .year(period.year)
              .month(period.month)
              .endOf('month'),
          }}
          storeIds={typeof storeId === 'number' ? [storeId] : undefined}
          trigger={shouldTrigger && step === 4 ? applyTrigger : 0}
          onLoaded={onLoadedGuard(4, 5)}
        />
        <LevelChangeSummary
          period={period}
          storeId={storeId}
          trigger={shouldTrigger && step === 5 ? applyTrigger : 0}
          onLoaded={onLoadedGuard(5, 6)}
        />
        <DownloadableTitle title="Análisis de usuarios activos del período por tags" />
      </div>
      <div className="mt-8 flex flex-row justify-evenly gap-8">
        <GenderDonutChart
          period={period}
          storeId={storeId}
          trigger={shouldTrigger && step === 6 ? applyTrigger : 0}
          compara={compara}
          compareStoreId={compareStoreId}
          onLoaded={onLoadedGuard(6, 7)}
        />
        <AgeDonutChart
          period={period}
          storeId={storeId}
          trigger={shouldTrigger && step === 7 ? applyTrigger : 0}
          compara={compara}
          compareStoreId={compareStoreId}
          onLoaded={onLoadedGuard(7, 8)}
        />
        <FoodPreferenceDonutChart
          period={period}
          storeId={storeId}
          trigger={shouldTrigger && step === 8 ? applyTrigger : 0}
          compara={compara}
          compareStoreId={compareStoreId}
          onLoaded={onLoadedGuard(8, 9)}
        />
      </div>
      <DownloadableTitle title="Resumen de análisis por tags" />
      <div className="mt-8 flex flex-col gap-8">
        <GenderDonutChartsByLevel
          period={period}
          storeId={storeId}
          trigger={shouldTrigger && step === 9 ? applyTrigger : 0}
          onLoaded={onLoadedGuard(9, 10)}
        />
        <AgeDonutChartsByLevel
          period={period}
          storeId={storeId}
          trigger={shouldTrigger && step === 10 ? applyTrigger : 0}
          onLoaded={onLoadedGuard(10, 11)}
        />
        <FoodPreferenceDonutChartsByLevel
          period={period}
          storeId={storeId}
          trigger={shouldTrigger && step === 11 ? applyTrigger : 0}
          onLoaded={onLoadedGuard(11, 12)}
        />
      </div>
    </div>
  )
}

export default ClientScanTab
